/**
 * Design System 2.0 tokens needed by the MUI side of the refresh.
 *
 * These duplicate values declared in `packages/design-system/src/styles/design-system.css`, which is regrettable
 * but deliberate: Tailwind v4 tree-shakes `@theme` variables it cannot see used, so an Emotion rule
 * referencing `var(--color-light-2)` only resolves for as long as some Tailwind class happens to
 * use that token too. (Verified in a production build: `--color-light-2` is emitted because the
 * button recipes use it, `--color-light-5` is not.) A silent colour drop is a worse failure than a
 * duplicated hex, so the MUI side keeps its own copy.
 *
 * Retiring the flag deletes this file along with the rest of the directory.
 */

/** `Color/Light/0` — plain white, the top of the light ramp. */
export const LIGHT_0 = '#ffffff';

/** `Color/Light/2` — the DS 2.0 border that replaces elevation on light surfaces. */
export const LIGHT_2 = '#e3e5e8';

/** `Color/Light/5` — secondary text on a dark surface: legible without competing with `Light/0`. */
export const LIGHT_5 = '#acb0b9';

/** `Color/Light/Background` — the page behind every light surface. Legacy uses `grey[25]`, #fcfcfd. */
export const LIGHT_BACKGROUND = '#f9fafa';

/**
 * `Color/Dark/0` — the base of the dark surface ramp, and the background of every dark region.
 *
 * Legacy dark screens use `dark[300]` (#201f1f), a near-black grey. DS 2.0 goes to true black.
 */
export const DARK_0 = '#000000';

/**
 * `Color/Dark/3` — the panel sitting on `Dark/0`. The live-room sidebars are drawn at this step.
 */
export const DARK_3 = '#17191c';

/** `Color/Dark/4` — one step up from `Dark/3`: inputs and cards nested inside a dark panel. */
export const DARK_4 = '#23252a';

/** `Color/Dark/5` — the divider/border on a dark surface, the dark counterpart of `Light/2`. */
export const DARK_5 = '#2f3237';

/**
 * `Color/Dark/6` — a border meant to be SEEN rather than merely to separate, one step lighter than
 * `Dark/5`. The DS text input draws its own border here in the `.dark` scope.
 */
export const DARK_6 = '#474b52';

/** `Color/Dark/7` — the lightest of the dark ramp, used for disabled text and icons. */
export const DARK_7 = '#5f646d';

/**
 * `Color/Blue/Primary` — the DS 2.0 accent, the fill of a primary button (`.ds-btn-primary-default`).
 *
 * The legacy `palette.primary.main` is `blue[600]` (#1570ef), a softer, greener blue. Nothing passes
 * a colour into `theme()`, so despite the signature that value is a constant rather than per-account
 * branding — swapping it on a control is a straight colour change, not an override of someone's
 * brand.
 */
export const BLUE_PRIMARY = '#004fff';

/** `Color/Blue/2` — a pale blue, legible as an icon or accent against the deep blues below. */
export const BLUE_2 = '#80bbff';

/** `Color/Blue/9` — the deep blue that carries white text, for accents on a dark surface. */
export const BLUE_9 = '#002880';

/** `Color/Blue/10` — quieter than `Blue/9`; the informational surface on a dark panel. */
export const BLUE_10 = '#00184d';

/*
 * Semantic roles (NTL-219), light mode. The constants above are the older `Light/*` and `Dark/*`
 * ramp; these name the ROLE a value serves, as the Figma library's "Modes" collection does, so a
 * reader can tell what a colour is for without opening Figma. Values read from Figma (file
 * `5aSkE0U4TeWbuIZDvccHjc`, Modes → Light) on 2026-10-05, and identical to the role declarations in
 * `packages/design-system/src/styles/design-system.css`. Kept as hex for the reason at the top of
 * this file. New MUI-side colours should name a role from here rather than a ramp step.
 */

/** `foreground/primary` — text and icons for the default (`neutral/900`). */
export const FOREGROUND_PRIMARY = '#0e0f11';

/**
 * `foreground/secondary` — text and icons one step back from the default (`neutral/700`).
 * Was `neutral/600` (`#61656b`, 5.86:1 on white) until 2026-10-07; `#4a4e53` is 8.38:1, which clears AAA.
 * Keep in step with `--foreground-secondary` in `packages/design-system/src/styles/design-system.css`.
 */
export const FOREGROUND_SECONDARY = '#4a4e53';

/**
 * `foreground/disabled` — `neutral/500`, 2.81:1 on white. Deliberately below AA (disabled is
 * WCAG-exempt) but still legible, and noticeably darker than the legacy `grey[300]` it replaces.
 */
export const FOREGROUND_DISABLED = '#8f9398';

/** `foreground/accent` — brand-blue text and icons for wayfinding (`blue/700`). Never status. */
export const FOREGROUND_ACCENT = '#0f3eac';

/** `foreground/accent/hover` (`blue/800`). */
export const FOREGROUND_ACCENT_HOVER = '#0f348a';

/** `foreground/accent/active` — pressed (`blue/900`). */
export const FOREGROUND_ACCENT_ACTIVE = '#092870';

/** `background/primary/hover` — the default surface, hovered (`neutral/100`). */
export const BACKGROUND_PRIMARY_HOVER = '#f4f5f7';

/** `background/primary/active` — the default surface, pressed (`neutral/200`). */
export const BACKGROUND_PRIMARY_ACTIVE = '#e8eaed';

/** `border/primary` — decorative edge for the default surface, one ramp step off it (`neutral/200`). */
export const BORDER_PRIMARY = '#e8eaed';

/** `border/control` — the boundary of an input, select or switch track (`neutral/300`). */
export const BORDER_CONTROL = '#d5d7db';

/**
 * `border/secondary/active` — the one border step that clears the 3:1 non-text floor on white
 * (`neutral/500`, 3.1:1). `border/primary` and `border/control` are both below it.
 */
export const BORDER_SECONDARY_ACTIVE = '#8f9398';

/** `background/disabled` — the fill of an unavailable control (`neutral/100`). */
export const BACKGROUND_DISABLED = '#f4f5f7';

/**
 * `background/accent-muted` — the tinted brand-blue surface for wayfinding (`blue/50`). Never status.
 * Takes `foreground/accent` on top.
 */
export const BACKGROUND_ACCENT_MUTED = '#e9f0ff';

/** `background/accent-muted/hover` (`blue/100`). */
export const BACKGROUND_ACCENT_MUTED_HOVER = '#cbddfe';

/** `background/accent-muted/active` — pressed (`blue/200`). */
export const BACKGROUND_ACCENT_MUTED_ACTIVE = '#afc9fd';

/**
 * `background/on-surface` — a translucent wash that works on any light surface. The DS ProgressBar
 * draws its track with it. A literal, not a ramp step, by the library's own definition.
 */
export const BACKGROUND_ON_SURFACE = 'rgba(0, 0, 0, 0.04)';
